import React, { useState, useEffect } from 'react';
import API from '../api/config';
import '../styles/Users.css';

const DEPARTMENTS = ['Chemistry', 'Physics', 'Mathematics', 'CELab'];
const ROLES = [
  { id: 'content_author', label: 'Content Author' },
  { id: 'hod',            label: 'HOD (Head of Department)' },
  { id: 'admin',          label: 'Admin' },
];

const EMPTY_FORM = { name: '', username: '', password: '', role_id: '', department: '' };

export default function Users() {
  const [users,          setUsers]          = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [showModal,      setShowModal]      = useState(false);
  const [showPass,       setShowPass]       = useState(false);
  const [error,          setError]          = useState('');
  const [success,        setSuccess]        = useState('');
  const [usernamePrefix, setUsernamePrefix] = useState('');
  const [form,           setForm]           = useState(EMPTY_FORM);
  const [editingId,      setEditingId]      = useState(null);
  const [confirmDelete,  setConfirmDelete]  = useState(null);

  const fetchUsers = async () => {
    try {
      const res = await API.get('/api/users');
      setUsers(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setUsernamePrefix('');
    setError(''); setSuccess('');
    setShowModal(true);
  };

  const openEdit = (u) => {
    setEditingId(u.user_id);
    const prefix = (u.username || '').replace('@jyothyit.ac.in', '');
    setUsernamePrefix(prefix);
    setForm({
      name      : u.name,
      username  : u.username,
      password  : '',
      role_id   : u.role_name || u.role,
      department: u.department || '',
    });
    setError(''); setSuccess('');
    setShowModal(true);
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handlePrefixChange = e => {
    const prefix = e.target.value.replace(/@.*/, '').replace(/\s/g, '').toLowerCase();
    setUsernamePrefix(prefix);
    setForm(f => ({ ...f, username: prefix ? prefix + '@jyothyit.ac.in' : '' }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!editingId && !form.username.endsWith('@jyothyit.ac.in')) {
      setError('Username must be an institutional email (@jyothyit.ac.in)'); return;
    }
    if (!form.department) {
      setError('Please select a department'); return;
    }
    try {
      if (editingId) {
        await API.put(`/api/users/${editingId}`, {
          name      : form.name,
          role      : form.role_id,
          department: form.department,
          password  : form.password || undefined,
        });
        setSuccess('✅ User updated successfully');
      } else {
        await API.post('/api/users', {
          name      : form.name,
          username  : form.username,
          password  : form.password,
          role      : form.role_id,
          department: form.department,
        });
        setSuccess(`✅ User created! Email: ${form.username}`);
      }
      fetchUsers();
      setTimeout(() => { setShowModal(false); setSuccess(''); }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save user');
    }
  };

  const handleToggle = async id => {
    try { await API.patch(`/api/users/${id}/toggle`, {}); fetchUsers(); }
    catch (err) { console.error(err); }
  };

  const handleDelete = async (u) => {
    try {
      await API.delete(`/api/users/${u.user_id}`);
      setConfirmDelete(null);
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete user');
    }
  };

  const roleLabel = r => {
    if (r === 'admin')          return 'Admin';
    if (r === 'content_author') return 'Content Author';
    if (r === 'hod')            return 'HOD';
    return r || '—';
  };

  const EyeIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  );

  const EyeOffIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );

  return (
    <div className="u-page">
      <div className="u-header">
        <h1 className="u-title">Users</h1>
        <button className="u-btn-add" onClick={openCreate}>+ Add User</button>
      </div>

      <div className="u-table-wrap">
        {loading ? <p className="u-loading">Loading...</p> : (
          <table className="u-table">
            <thead>
              <tr>
                <th className="u-col-no">Sl. No.</th>
                <th className="u-col-name">Name</th>
                <th className="u-col-email">UserID</th>
                <th className="u-col-dept">Department</th>
                <th className="u-col-role">Role</th>
                <th className="u-col-status">Status</th>
                <th className="u-col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr><td colSpan="7" className="u-empty">No users found</td></tr>
              ) : users.map((u, i) => (
                <tr key={u.user_id} className="u-row">
                  <td className="u-td-no">{i + 1}</td>
                  <td className="u-td-name">{u.name}</td>
                  <td className="u-td-email">{u.username}</td>
                  <td className="u-td-dept">{u.department || '—'}</td>
                  <td className="u-td-role">{roleLabel(u.role_name)}</td>
                  <td>
                    <span className={u.is_active ? 'u-status-active' : 'u-status-inactive'}>
                      {u.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="u-td-actions">
                    <button className="u-act u-act--edit" onClick={() => openEdit(u)}>Edit</button>
                    {u.role_name !== 'admin' && (
                      <button
                        className={u.is_active ? 'u-act u-act--deactivate' : 'u-act u-act--activate'}
                        onClick={() => handleToggle(u.user_id)}
                      >
                        {u.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                    )}
                    {u.role_name !== 'admin' && (
                      <button className="u-act u-act--delete" onClick={() => setConfirmDelete(u)}>Delete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create / Edit Modal */}
      {showModal && (
        <div className="u-overlay">
          <div className="u-modal">
            <div className="u-modal-head">
              <h2 className="u-modal-title">{editingId ? 'Edit User' : 'Create New User'}</h2>
              <button className="u-modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>

            {error   && <div className="u-error">{error}</div>}
            {success && <div className="u-success">{success}</div>}

            <form onSubmit={handleSubmit}>
              <div className="u-field">
                <label className="u-label">Full Name *</label>
                <input className="u-input" type="text" name="name"
                  value={form.name} onChange={handleChange}
                  placeholder="e.g. Pushpalatha K" required />
              </div>

              <div className="u-field">
                <label className="u-label">Role *</label>
                <select className="u-input" name="role_id"
                  value={form.role_id} onChange={handleChange} required>
                  <option value="">— Select Role —</option>
                  {ROLES.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
                </select>
              </div>

              <div className="u-field">
                <label className="u-label">Department *</label>
                <select className="u-input" name="department"
                  value={form.department} onChange={handleChange} required>
                  <option value="">— Select Department —</option>
                  {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>

              {!editingId && (
                <div className="u-field">
                  <label className="u-label">UserID *</label>
                  <div className="u-email-wrap">
                    <input className="u-input u-input--email" type="text"
                      value={usernamePrefix} onChange={handlePrefixChange}
                      placeholder="username" required />
                    <span className="u-email-suffix">@jyothyit.ac.in</span>
                  </div>
                  {form.username && (
                    <p className="u-email-hint">Login email: <strong>{form.username}</strong></p>
                  )}
                </div>
              )}

              <div className="u-field">
                <label className="u-label">
                  {editingId ? 'New Password (leave blank to keep current)' : 'Password *'}
                </label>
                <div className="u-pass-wrap">
                  <input className="u-input" type={showPass ? 'text' : 'password'}
                    name="password" value={form.password} onChange={handleChange}
                    placeholder={editingId ? 'Leave blank to keep unchanged' : 'Minimum 6 characters'}
                    required={!editingId} />
                  <button type="button" className="u-pass-toggle"
                    onClick={() => setShowPass(p => !p)} tabIndex={-1}>
                    {showPass ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>

              <div className="u-modal-actions">
                <button type="button" className="u-btn-cancel"
                  onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="u-btn-submit">
                  {editingId ? 'Save Changes' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {confirmDelete && (
        <div className="u-overlay">
          <div className="u-modal u-modal--sm">
            <h2 className="u-modal-title">Delete User?</h2>
            <p className="u-confirm-text">
              Are you sure you want to delete <strong>{confirmDelete.name}</strong>? This cannot be undone.
            </p>
            <div className="u-modal-actions">
              <button className="u-btn-cancel" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="u-btn-delete" onClick={() => handleDelete(confirmDelete)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}